import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { 
  Images, 
  Expand, 
  Calendar, 
  X, 
  Search, 
  Filter, 
  ExternalLink, 
  ChevronRight, 
  ChevronLeft, 
  Layers, 
  Camera, 
  Play, 
  Pause, 
  ZoomIn, 
  ZoomOut, 
  Film 
} from 'lucide-react';
import CustomSpinner from '../../components/customerSpinner/CustomSpinner';
import './PhotoGallery.css';

const config = require('../../config.json');

// Helper to safely extract 4-digit year from date string or title
function extractYear(dateStr, titleStr) {
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d.getFullYear()) && d.getFullYear() >= 1970 && d.getFullYear() <= 2099) {
      return d.getFullYear().toString();
    }
    const match = dateStr.match(/\b(19\d\d|20\d\d)\b/);
    if (match) return match[1];
  }
  if (titleStr) {
    const matchTitle = titleStr.match(/\b(19\d\d|20\d\d)\b/);
    if (matchTitle) return matchTitle[1];
  }
  return 'Archive';
}

// Format date into human-readable string (e.g. "15 Oct 2024")
function formatDateDisplay(dateStr) {
  if (!dateStr) return 'Recent Event';
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return dateStr;
}

export default function PhotoGallery(props) {
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [selectedProject, setSelectedProject] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Lightbox Enhancement States
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showFilmstrip, setShowFilmstrip] = useState(true);
  const [isImgLoading, setIsImgLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const touchStartX = useRef(null);
  const thumbnailContainerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      setLoaded(false);
      const fileName = 'images/gallery/';
      const bucketName = 'images.vidyajyoticollege.in';

      const response = await axios.get(
        `${config.api.invokeURL}/gallery?fileName=${fileName}&bucketName=${bucketName}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );

      if (response.data && Array.isArray(response.data)) {
        // Tag each item with its extracted year and sort chronologically (newest first)
        const enriched = response.data.map((item) => ({
          ...item,
          year: extractYear(item.projectDate, item.projectName)
        }));

        enriched.sort((a, b) => {
          const dateA = new Date(a.projectDate || 0);
          const dateB = new Date(b.projectDate || 0);
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
            return (b.year || '').localeCompare(a.year || '');
          }
          return dateB - dateA;
        });

        setGalleryImages(enriched);
      }
    } catch (error) {
      console.error("Error loading photo gallery:", error);
    } finally {
      setLoaded(true);
    }
  };

  // Compute available distinct years sorted descending
  const availableYears = useMemo(() => {
    const yearsMap = new Map();
    galleryImages.forEach((item) => {
      const yr = item.year || 'Archive';
      yearsMap.set(yr, (yearsMap.get(yr) || 0) + 1);
    });

    const sortedYears = Array.from(yearsMap.keys()).sort((a, b) => {
      if (a === 'Archive') return 1;
      if (b === 'Archive') return -1;
      return parseInt(b, 10) - parseInt(a, 10);
    });

    return sortedYears.map((yr) => ({
      year: yr,
      count: yearsMap.get(yr)
    }));
  }, [galleryImages]);

  // Total photo count calculation
  const totalPhotosCount = useMemo(() => {
    return galleryImages.reduce((sum, item) => sum + (item.fileName ? item.fileName.length : 0), 0);
  }, [galleryImages]);

  // Filter gallery items based on selected year and search query
  const filteredAlbums = useMemo(() => {
    return galleryImages.filter((item) => {
      const matchesYear = selectedYear === 'ALL' || item.year === selectedYear;
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch = !query || 
        (item.projectName && item.projectName.toLowerCase().includes(query)) ||
        (item.projectDate && item.projectDate.toLowerCase().includes(query)) ||
        (item.year && item.year.toLowerCase().includes(query));
      return matchesYear && matchesSearch;
    });
  }, [galleryImages, selectedYear, searchQuery]);

  // Group filtered albums by year (useful for the "ALL" view) with latest years first
  const groupedByYear = useMemo(() => {
    const groups = {};
    filteredAlbums.forEach((item) => {
      const yr = item.year || 'Archive';
      if (!groups[yr]) groups[yr] = [];
      groups[yr].push(item);
    });

    const sortedYearKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Archive') return 1;
      if (b === 'Archive') return -1;
      return parseInt(b, 10) - parseInt(a, 10);
    });

    return sortedYearKeys.map((yearKey) => ({
      yearKey,
      albums: groups[yearKey]
    }));
  }, [filteredAlbums]);

  // Handle opening an album
  const assignProject = (event, item) => {
    if (event) event.preventDefault();
    setSelectedProject(item.fileName || []);
    setSelectedTitle(item.projectName || 'Photo Gallery');
    setSelectedDate(item.projectDate || '');
    setCurrentIndex(0);
    setIsPlaying(false);
    setIsZoomed(false);
    setIsImgLoading(true);
    setProgress(0);
    setCollapsed(false);
  };

  // Close lightbox modal
  const handleClose = useCallback(() => {
    setIsPlaying(false);
    setIsZoomed(false);
    setProgress(0);
    setCollapsed(true);
  }, []);

  // Navigation handlers
  const handlePrev = useCallback(() => {
    if (selectedProject.length <= 1) return;
    setIsImgLoading(true);
    setIsZoomed(false);
    setProgress(0);
    setCurrentIndex((prev) => (prev === 0 ? selectedProject.length - 1 : prev - 1));
  }, [selectedProject.length]);

  const handleNext = useCallback(() => {
    if (selectedProject.length <= 1) return;
    setIsImgLoading(true);
    setIsZoomed(false);
    setProgress(0);
    setCurrentIndex((prev) => (prev === selectedProject.length - 1 ? 0 : prev + 1));
  }, [selectedProject.length]);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
    setProgress(0);
  }, []);

  const toggleZoom = useCallback(() => {
    setIsZoomed((prev) => !prev);
  }, []);

  const toggleFilmstrip = useCallback(() => {
    setShowFilmstrip((prev) => !prev);
  }, []);

  // Preload adjacent photos for instantaneous navigation
  useEffect(() => {
    if (!collapsed && selectedProject.length > 1) {
      const nextIdx = (currentIndex + 1) % selectedProject.length;
      const prevIdx = (currentIndex - 1 + selectedProject.length) % selectedProject.length;
      if (selectedProject[nextIdx]) {
        const preloadNext = new Image();
        preloadNext.src = `${config.s3.ImageinvokeURL}${selectedProject[nextIdx]}`;
      }
      if (selectedProject[prevIdx]) {
        const preloadPrev = new Image();
        preloadPrev.src = `${config.s3.ImageinvokeURL}${selectedProject[prevIdx]}`;
      }
    }
  }, [collapsed, currentIndex, selectedProject]);

  // Auto-scroll active thumbnail into view in the filmstrip
  useEffect(() => {
    if (!collapsed && thumbnailContainerRef.current) {
      const activeThumb = thumbnailContainerRef.current.querySelector(`.vj-lightbox-thumb[data-index="${currentIndex}"]`);
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [collapsed, currentIndex]);

  // Auto-advance slideshow timer with smooth progress bar
  useEffect(() => {
    if (collapsed || !isPlaying || selectedProject.length <= 1) {
      setProgress(0);
      return;
    }

    const intervalDuration = 3500; // 3.5s per slide
    const step = 50; // update every 50ms
    const stepPercent = (step / intervalDuration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev + stepPercent >= 100) {
          handleNext();
          return 0;
        }
        return prev + stepPercent;
      });
    }, step);

    return () => clearInterval(timer);
  }, [collapsed, isPlaying, selectedProject.length, handleNext]);

  // Lock body scroll when modal is active
  useEffect(() => {
    if (!collapsed) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [collapsed]);

  // Keyboard navigation shortcuts
  useEffect(() => {
    if (collapsed) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        toggleZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [collapsed, handleClose, handlePrev, handleNext, togglePlay, toggleZoom]);

  // Touch swipe support for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const endX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - endX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = null;
  };

  return (
    <div className="vj-photo-gallery-page py-4 py-md-5" style={{ background: '#f8fafc', minHeight: '90vh' }}>
      <Helmet>
        <title>Photo Gallery &amp; Events - Vidyajyoti College of Theology</title>
        <meta name="description" content="Explore year-wise photo archives of academic convocations, seminars, liturgical celebrations, and campus life at Vidyajyoti." />
        <link rel="canonical" href="/gallery/photos" />
      </Helmet>

      <div className="container" style={{ maxWidth: '1200px' }}>
        {/* Page Hero Header */}
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '780px' }}>
          <div 
            className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-3" 
            style={{ 
              background: 'rgba(121, 3, 29, 0.08)', 
              color: '#79031d', 
              fontSize: '0.85rem', 
              fontWeight: 700, 
              border: '1px solid rgba(121, 3, 29, 0.18)' 
            }}
          >
            <Camera size={15} /> CHRONICLES &amp; CAMPUS LIFE
          </div>
          <h1 className="display-6 fw-bold mb-2" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Campus Photo Gallery
          </h1>
          <p className="text-muted small mb-0" style={{ fontSize: '0.94rem', lineHeight: '1.6' }}>
            Archiving decades of theological scholarship, solemn convocations, spiritual celebrations, and pastoral fieldwork ministries in Delhi and across India.
          </p>
        </div>

        {/* Search & Statistics Bar */}
        <div className="card border-0 rounded-4 shadow-sm p-3 p-md-4 bg-white mb-4">
          <div className="row g-3 align-items-center justify-content-between">
            {/* Search Input */}
            <div className="col-md-6 col-lg-5">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0 text-muted">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control rounded-end-3 py-2 border-start-0"
                  placeholder="Search events, convocations, seminars..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-light border border-start-0 text-muted"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Metrics Chips */}
            <div className="col-md-6 col-lg-6 d-flex flex-wrap align-items-center justify-content-md-end gap-2">
              <div className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-semibold small d-inline-flex align-items-center gap-1.5">
                <Layers size={14} className="text-primary" />
                <span>{filteredAlbums.length} Albums</span>
              </div>
              <div className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-semibold small d-inline-flex align-items-center gap-1.5">
                <Images size={14} className="text-burgundy" style={{ color: '#79031d' }} />
                <span>{totalPhotosCount} Photographs</span>
              </div>
              <div className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-semibold small d-inline-flex align-items-center gap-1.5">
                <Calendar size={14} className="text-warning" />
                <span>{availableYears.length} Academic Years</span>
              </div>
            </div>
          </div>

          {/* Year Filter Tabs */}
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex align-items-center gap-2 overflow-auto pb-1" style={{ whiteSpace: 'nowrap', scrollbarWidth: 'thin' }}>
              <span className="text-muted small fw-bold me-1 d-inline-flex align-items-center gap-1">
                <Filter size={13} /> Filter Year:
              </span>

              {/* All Years Tab */}
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold transition-all ${
                  selectedYear === 'ALL'
                    ? 'text-white shadow-sm'
                    : 'btn-outline-secondary bg-white text-dark'
                }`}
                style={{
                  background: selectedYear === 'ALL' ? '#1a2b56' : '#ffffff',
                  borderColor: selectedYear === 'ALL' ? '#1a2b56' : '#e2e8f0',
                  fontSize: '0.82rem'
                }}
                onClick={() => setSelectedYear('ALL')}
              >
                All Years ({galleryImages.length})
              </button>

              {/* Individual Year Tabs */}
              {availableYears.map((item) => {
                const isSelected = selectedYear === item.year;
                return (
                  <button
                    key={item.year}
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold transition-all ${
                      isSelected
                        ? 'text-white shadow-sm'
                        : 'btn-outline-secondary bg-white text-dark'
                    }`}
                    style={{
                      background: isSelected ? '#1a2b56' : '#ffffff',
                      borderColor: isSelected ? '#1a2b56' : '#e2e8f0',
                      fontSize: '0.82rem'
                    }}
                    onClick={() => setSelectedYear(item.year)}
                  >
                    {item.year} <span className="opacity-75">({item.count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        {!loaded && (
          <div className="text-center py-5">
            <CustomSpinner />
          </div>
        )}

        {/* Fullscreen Premium Lightbox Modal */}
        {!collapsed && (
          <div className="vj-lightbox-overlay" role="dialog" aria-modal="true">
            {/* Slideshow Progress Bar */}
            {isPlaying && selectedProject.length > 1 && (
              <div 
                className="vj-lightbox-progress" 
                style={{ width: `${progress}%` }} 
              />
            )}

            {/* Header / Top Toolbar */}
            <div className="vj-lightbox-header">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <h5 className="vj-lightbox-title" title={selectedTitle}>
                  {selectedTitle}
                </h5>
                <div className="vj-lightbox-badges">
                  {selectedDate && (
                    <span className="vj-lightbox-badge">
                      <Calendar size={12} className="text-warning" />
                      {formatDateDisplay(selectedDate)}
                    </span>
                  )}
                  {selectedProject.length > 0 && (
                    <span className="vj-lightbox-badge counter">
                      <Images size={12} />
                      Photo {currentIndex + 1} of {selectedProject.length}
                    </span>
                  )}
                </div>
              </div>

              <div className="vj-lightbox-actions">
                {selectedProject.length > 1 && (
                  <button
                    type="button"
                    className={`vj-lightbox-btn ${isPlaying ? 'active' : ''}`}
                    onClick={togglePlay}
                    title={isPlaying ? 'Pause Slideshow (Space)' : 'Play Slideshow (Space)'}
                    aria-label="Toggle Slideshow"
                  >
                    {isPlaying ? <Pause size={15} /> : <Play size={15} />}
                    <span className="d-none d-sm-inline">{isPlaying ? 'Pause' : 'Slideshow'}</span>
                    <span className="vj-kbd-shortcut">Space</span>
                  </button>
                )}

                <button
                  type="button"
                  className={`vj-lightbox-btn ${isZoomed ? 'active' : ''}`}
                  onClick={toggleZoom}
                  title={isZoomed ? 'Zoom Out (Z)' : 'Zoom In (Z)'}
                  aria-label="Toggle Zoom"
                >
                  {isZoomed ? <ZoomOut size={15} /> : <ZoomIn size={15} />}
                  <span className="d-none d-md-inline">{isZoomed ? 'Fit' : 'Zoom'}</span>
                  <span className="vj-kbd-shortcut">Z</span>
                </button>

                {selectedProject.length > 1 && (
                  <button
                    type="button"
                    className={`vj-lightbox-btn ${showFilmstrip ? 'active' : ''}`}
                    onClick={toggleFilmstrip}
                    title={showFilmstrip ? 'Hide Thumbnails' : 'Show Thumbnails'}
                    aria-label="Toggle Thumbnails"
                  >
                    <Film size={15} />
                    <span className="d-none d-md-inline">Filmstrip</span>
                  </button>
                )}

                {selectedProject[currentIndex] && (
                  <a
                    href={`${config.s3.ImageinvokeURL}${selectedProject[currentIndex]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vj-lightbox-btn text-decoration-none"
                    title="Open original high-res photo in new tab"
                    aria-label="Open original image"
                  >
                    <ExternalLink size={15} />
                    <span className="d-none d-lg-inline">Original</span>
                  </a>
                )}

                <button
                  type="button"
                  className="vj-lightbox-btn close-btn"
                  onClick={handleClose}
                  title="Close Lightbox (Esc)"
                  aria-label="Close"
                >
                  <X size={17} />
                  <span className="d-none d-sm-inline">Close</span>
                  <span className="vj-kbd-shortcut">Esc</span>
                </button>
              </div>
            </div>

            {/* Main Viewing Stage */}
            <div 
              className="vj-lightbox-stage"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Previous Button */}
              {selectedProject.length > 1 && (
                <button
                  type="button"
                  className="vj-lightbox-nav prev"
                  onClick={handlePrev}
                  aria-label="Previous photo"
                  title="Previous (←)"
                >
                  <ChevronLeft size={28} />
                </button>
              )}

              {/* Next Button */}
              {selectedProject.length > 1 && (
                <button
                  type="button"
                  className="vj-lightbox-nav next"
                  onClick={handleNext}
                  aria-label="Next photo"
                  title="Next (→)"
                >
                  <ChevronRight size={28} />
                </button>
              )}

              {/* Image Loading Ring */}
              {isImgLoading && (
                <div className="vj-lightbox-loader">
                  <div className="vj-spinner-ring" />
                  <span>Loading high-res photo...</span>
                </div>
              )}

              {/* Main Photo Display */}
              {selectedProject.length > 0 && selectedProject[currentIndex] ? (
                <div className="vj-lightbox-image-wrapper">
                  <img
                    key={selectedProject[currentIndex]}
                    src={`${config.s3.ImageinvokeURL}${selectedProject[currentIndex]}`}
                    alt={`${selectedTitle} - Photo ${currentIndex + 1}`}
                    className={`vj-lightbox-img ${isZoomed ? 'zoomed' : ''} ${isImgLoading ? 'loading' : ''}`}
                    onLoad={() => setIsImgLoading(false)}
                    onClick={() => {
                      if (isZoomed) setIsZoomed(false);
                    }}
                  />
                </div>
              ) : (
                <div className="text-muted text-center p-5">
                  <Images size={48} className="mb-2 opacity-50" />
                  <p className="mb-0">No photographs available in this album.</p>
                </div>
              )}

              {/* Bottom Caption Pill */}
              {selectedTitle && selectedProject.length > 0 && (
                <div className="vj-lightbox-caption">
                  {selectedTitle} — Photo {currentIndex + 1} of {selectedProject.length}
                </div>
              )}
            </div>

            {/* Bottom Interactive Thumbnail Filmstrip */}
            {showFilmstrip && selectedProject.length > 1 && (
              <div className="vj-lightbox-filmstrip-wrapper">
                <div className="vj-lightbox-filmstrip" ref={thumbnailContainerRef}>
                  {selectedProject.map((thumbFile, idx) => {
                    const isActive = idx === currentIndex;
                    return (
                      <div
                        key={idx}
                        data-index={idx}
                        className={`vj-lightbox-thumb ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          if (idx !== currentIndex) {
                            setIsImgLoading(true);
                            setIsZoomed(false);
                            setProgress(0);
                            setCurrentIndex(idx);
                          }
                        }}
                        title={`View photo ${idx + 1}`}
                      >
                        <img
                          src={`${config.s3.ImageinvokeURL}${thumbFile}`}
                          alt={`Thumb ${idx + 1}`}
                          loading="lazy"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty Search Results Feedback */}
        {loaded && filteredAlbums.length === 0 && (
          <div className="card border-0 rounded-4 shadow-sm p-5 text-center bg-white my-4">
            <div 
              className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3 bg-light text-muted"
              style={{ width: '60px', height: '60px' }}
            >
              <Images size={30} />
            </div>
            <h5 className="fw-bold text-dark mb-1">No Albums Found</h5>
            <p className="text-muted small mb-3">
              No photo albums match your search query &quot;<strong>{searchQuery}</strong>&quot; in year <strong>{selectedYear}</strong>.
            </p>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4 py-2 small fw-semibold"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedYear('ALL');
                }}
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}

        {/* Gallery Content Rendered Year-Wise */}
        {loaded && filteredAlbums.length > 0 && (
          <div>
            {selectedYear === 'ALL' ? (
              // Grouped Sections for ALL YEARS (latest year first)
              groupedByYear.map(({ yearKey, albums: albumsInYear }) => {
                return (
                  <div key={yearKey} className="mb-5">
                    {/* Section Year Header */}
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <span 
                          className="badge rounded-pill px-3 py-1.5 text-white fw-bold shadow-xs"
                          style={{ background: '#1a2b56', fontSize: '0.85rem' }}
                        >
                          {yearKey}
                        </span>
                        <h4 className="fw-bold mb-0 text-dark" style={{ fontSize: '1.25rem', fontFamily: "'Outfit', sans-serif" }}>
                          Academic Year {yearKey}
                        </h4>
                      </div>
                      <span className="text-muted small fw-semibold">
                        {albumsInYear.length} {albumsInYear.length === 1 ? 'Event' : 'Events'}
                      </span>
                    </div>

                    {/* Albums Grid for this Year */}
                    <Row className="g-4">
                      {albumsInYear.map((item, index) => {
                        const coverImg = item.fileName && item.fileName.length > 0 ? item.fileName[0] : '';
                        const photoCount = item.fileName ? item.fileName.length : 0;

                        return (
                          <Col md={6} lg={4} key={index}>
                            <div
                              className="vj-modern-card h-100 cursor-pointer overflow-hidden position-relative rounded-4 border shadow-xs bg-white transition-all d-flex flex-column"
                              onClick={(e) => assignProject(e, item)}
                              style={{ transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                            >
                              <div className="position-relative" style={{ height: '220px', overflow: 'hidden' }}>
                                {coverImg ? (
                                  <img
                                    src={encodeURI(`${config.s3.ImageinvokeURL}${coverImg}`)}
                                    alt={item.projectName}
                                    className="w-100 h-100 object-fit-cover transition-zoom"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                                    <Images size={36} className="text-muted" />
                                  </div>
                                )}

                                {/* Photo Count Badge */}
                                <div
                                  className="position-absolute bottom-0 end-0 m-2.5 px-2.5 py-1 rounded-pill small fw-bold text-white d-flex align-items-center gap-1 shadow-sm"
                                  style={{ background: 'rgba(15, 28, 63, 0.8)', backdropFilter: 'blur(6px)', fontSize: '0.78rem' }}
                                >
                                  <Images size={13} /> {photoCount} Photos
                                </div>

                                <div className="vj-card-media-overlay" />
                              </div>

                              <div className="p-3.5 d-flex flex-column justify-content-between flex-grow-1">
                                <div>
                                  <span className="badge bg-light text-secondary border small mb-2">
                                    {item.year}
                                  </span>
                                  <h5 className="fw-bold text-dark mb-1 line-clamp-2" style={{ fontSize: '1.02rem', lineHeight: '1.4' }}>
                                    {item.projectName}
                                  </h5>
                                </div>

                                <div className="d-flex align-items-center justify-content-between text-muted small mt-3 pt-2.5 border-top">
                                  <span className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem' }}>
                                    <Calendar size={13} className="text-primary" /> {formatDateDisplay(item.projectDate)}
                                  </span>
                                  <span className="text-burgundy fw-bold d-flex align-items-center gap-1" style={{ color: '#79031d', fontSize: '0.82rem' }}>
                                    View Album <Expand size={12} />
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              // Filtered Grid for SINGLE SELECTED YEAR
              <div>
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <h4 className="fw-bold mb-0 text-dark" style={{ fontSize: '1.3rem', fontFamily: "'Outfit', sans-serif" }}>
                    Year {selectedYear} Albums ({filteredAlbums.length})
                  </h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none text-primary fw-semibold p-0"
                    onClick={() => setSelectedYear('ALL')}
                  >
                    View All Years →
                  </button>
                </div>

                <Row className="g-4">
                  {filteredAlbums.map((item, index) => {
                    const coverImg = item.fileName && item.fileName.length > 0 ? item.fileName[0] : '';
                    const photoCount = item.fileName ? item.fileName.length : 0;

                    return (
                      <Col md={6} lg={4} key={index}>
                        <div
                          className="vj-modern-card h-100 cursor-pointer overflow-hidden position-relative rounded-4 border shadow-xs bg-white transition-all d-flex flex-column"
                          onClick={(e) => assignProject(e, item)}
                          style={{ transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                        >
                          <div className="position-relative" style={{ height: '220px', overflow: 'hidden' }}>
                            {coverImg ? (
                              <img
                                src={encodeURI(`${config.s3.ImageinvokeURL}${coverImg}`)}
                                alt={item.projectName}
                                className="w-100 h-100 object-fit-cover transition-zoom"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                                <Images size={36} className="text-muted" />
                              </div>
                            )}

                            {/* Photo Count Badge */}
                            <div
                              className="position-absolute bottom-0 end-0 m-2.5 px-2.5 py-1 rounded-pill small fw-bold text-white d-flex align-items-center gap-1 shadow-sm"
                              style={{ background: 'rgba(15, 28, 63, 0.8)', backdropFilter: 'blur(6px)', fontSize: '0.78rem' }}
                            >
                              <Images size={13} /> {photoCount} Photos
                            </div>

                            <div className="vj-card-media-overlay" />
                          </div>

                          <div className="p-3.5 d-flex flex-column justify-content-between flex-grow-1">
                            <div>
                              <span className="badge bg-light text-secondary border small mb-2">
                                {item.year}
                              </span>
                              <h5 className="fw-bold text-dark mb-1 line-clamp-2" style={{ fontSize: '1.02rem', lineHeight: '1.4' }}>
                                {item.projectName}
                              </h5>
                            </div>

                            <div className="d-flex align-items-center justify-content-between text-muted small mt-3 pt-2.5 border-top">
                              <span className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem' }}>
                                <Calendar size={13} className="text-primary" /> {formatDateDisplay(item.projectDate)}
                              </span>
                              <span className="text-burgundy fw-bold d-flex align-items-center gap-1" style={{ color: '#79031d', fontSize: '0.82rem' }}>
                                View Album <Expand size={12} />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
